import { useEffect, useState } from 'react';
import { z } from 'zod';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { assetUrl, IMG } from '@/lib/assets';
import { getDateAndTime } from '@/lib/datetime';
import { fontOptionsSchema, fontStyleToCss } from '@/lib/fontStyle';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A clock: time on the left, an optional logo in the middle, the date on the right. Port of
 * `dateWidget/dateWidget.vue`. Present in all seven deployed configs.
 *
 * Takes no topic — it is driven entirely by the local clock.
 */

const optionsSchema = z.object({
  layout: widgetLayoutSchema.optional(),
  /** Resolved from `img/logos/`. Absent renders an empty middle column, not a broken image. */
  logoFile: z.string().optional(),
  /** Font of both text boxes. Named `textStyle` here and `…Font` everywhere else. */
  textStyle: fontOptionsSchema.optional(),
});

export function DateWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const { className, style } = useWidgetLayout(opts?.layout);
  const [date, time] = useDateAndTime();

  if (!parsed.success) {
    warnOnce(
      `date-widget-options:${componentId}`,
      '[signage] DateWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  // `parsed.data` rather than `opts` below: TypeScript does not relate the narrowing of
  // `parsed` to the separate const the hooks above had to read from.
  const { logoFile, textStyle: textFont } = parsed.data;
  const textStyle = fontStyleToCss(textFont, { fontSize: '1.2rem' });
  const logoUrl = logoFile ? assetUrl(`${IMG.logos}${logoFile}`) : undefined;

  return (
    <div className={className} style={style}>
      <div className="date-container flex items-center w-full text-center">
        <div className="text-box flex-1 text-left" style={textStyle}>
          {time}
        </div>
        <div className="flex-1">
          {logoUrl !== undefined && <img src={logoUrl} className="m-auto w-60" alt="" />}
        </div>
        <div className="text-box flex-1 text-right" style={textStyle}>
          {date}
        </div>
      </div>
    </div>
  );
}

/**
 * `[date, time]`, refreshed every second.
 *
 * The interval is the reason this is a hook and not two inline `useState`s: legacy
 * `dateWidget.vue` called `setInterval` in `setup()` and never cleared it (W-5), so a display
 * accumulated one live timer per mount — harmless in the flat layout, a leak in SYM-492's
 * slider, where widgets are mounted and unmounted for weeks.
 *
 * A 1Hz `setState` is cheap enough not to be worth optimising: it re-renders three `div`s.
 * The one thing it must not do is start at empty — legacy left both strings `''` until the
 * first tick, which is a visible blank second on every boot and every slide change. The state
 * is seeded from `getDateAndTime` instead.
 */
function useDateAndTime(): [date: string, time: string] {
  const [dateAndTime, setDateAndTime] = useState(getDateAndTime);

  useEffect(() => {
    const interval = setInterval(() => {
      setDateAndTime(getDateAndTime());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return dateAndTime;
}
